import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Calculator, Landmark, PiggyBank, Building2, FileText, Banknote, Globe2,
  ExternalLink, Receipt, ScrollText, ShieldCheck, Briefcase, GraduationCap, Scale,
  TrendingUp, Coins, Home, Percent, BadgeIndianRupee, LineChart, Wallet
} from "lucide-react";

const INR = (n) => `₹${(Math.round(n * 100) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/* ---------- EMI ---------- */
function EMICalc() {
  const [P, setP] = useState(1000000);
  const [R, setR] = useState(9.5);
  const [N, setN] = useState(20);
  const r = R / 12 / 100;
  const n = N * 12;
  const emi = useMemo(() => (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) || 0, [P, r, n]);
  const total = emi * n;
  const interest = total - P;

  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="emi-calc">
      <div className="space-y-4">
        <div><Label>Loan Amount (₹)</Label><Input type="number" value={P} onChange={e=>setP(Number(e.target.value))} data-testid="emi-amount"/></div>
        <div><Label>Interest Rate (% p.a.)</Label><Input type="number" step="0.1" value={R} onChange={e=>setR(Number(e.target.value))} data-testid="emi-rate"/></div>
        <div><Label>Tenure (years)</Label><Input type="number" value={N} onChange={e=>setN(Number(e.target.value))} data-testid="emi-tenure"/></div>
      </div>
      <div className="border border-slate-200 rounded p-6 bg-slate-50">
        <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">Monthly EMI</div>
        <div className="font-display text-5xl font-black text-slate-900 mt-1" data-testid="emi-result">{INR(emi)}</div>
        <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
          <div><div className="text-slate-500">Principal</div><div className="font-bold">{INR(P)}</div></div>
          <div><div className="text-slate-500">Total Interest</div><div className="font-bold text-blue-700">{INR(interest)}</div></div>
          <div className="col-span-2"><div className="text-slate-500">Total Payment ({n} months)</div><div className="font-bold">{INR(total)}</div></div>
        </div>
      </div>
    </div>
  );
}

/* ---------- FD ---------- */
function FDCalc() {
  const [P, setP] = useState(500000);
  const [R, setR] = useState(7.25);
  const [N, setN] = useState(5);
  const [F, setF] = useState(4); // compounding per year
  const A = useMemo(() => P * Math.pow(1 + R / 100 / F, F * N), [P, R, N, F]);
  const interest = A - P;
  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="fd-calc">
      <div className="space-y-4">
        <div><Label>Deposit Amount (₹)</Label><Input type="number" value={P} onChange={e=>setP(Number(e.target.value))} data-testid="fd-amount"/></div>
        <div><Label>Interest Rate (% p.a.)</Label><Input type="number" step="0.05" value={R} onChange={e=>setR(Number(e.target.value))} data-testid="fd-rate"/></div>
        <div><Label>Tenure (years)</Label><Input type="number" step="0.5" value={N} onChange={e=>setN(Number(e.target.value))} data-testid="fd-tenure"/></div>
        <div><Label>Compounding (per year): {F}</Label>
          <div className="flex gap-2 mt-2">
            {[1,2,4,12].map(x => <Button key={x} type="button" variant={F===x?"default":"outline"} size="sm" onClick={()=>setF(x)}>{x===1?"Yearly":x===2?"Half":x===4?"Quarterly":"Monthly"}</Button>)}
          </div>
        </div>
      </div>
      <div className="border border-slate-200 rounded p-6 bg-slate-50">
        <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">Maturity Value</div>
        <div className="font-display text-5xl font-black text-slate-900 mt-1" data-testid="fd-result">{INR(A)}</div>
        <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
          <div><div className="text-slate-500">Invested</div><div className="font-bold">{INR(P)}</div></div>
          <div><div className="text-slate-500">Interest Earned</div><div className="font-bold text-blue-700">{INR(interest)}</div></div>
        </div>
      </div>
    </div>
  );
}

/* ---------- RD ---------- */
function RDCalc() {
  const [M, setM] = useState(10000); // monthly deposit
  const [R, setR] = useState(7.0);
  const [N, setN] = useState(5);
  // RD with quarterly compounding (standard Indian bank RD formula)
  const i = R / 400; // quarterly rate
  const months = N * 12;
  // Approx using monthly compounding for simplicity & accuracy
  const im = R / 12 / 100;
  const A = useMemo(() => M * ((Math.pow(1 + im, months) - 1) / im) * (1 + im), [M, im, months]);
  const invested = M * months;
  const interest = A - invested;
  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="rd-calc">
      <div className="space-y-4">
        <div><Label>Monthly Deposit (₹)</Label><Input type="number" value={M} onChange={e=>setM(Number(e.target.value))} data-testid="rd-amount"/></div>
        <div><Label>Interest Rate (% p.a.)</Label><Input type="number" step="0.05" value={R} onChange={e=>setR(Number(e.target.value))} data-testid="rd-rate"/></div>
        <div><Label>Tenure (years)</Label><Input type="number" step="0.5" value={N} onChange={e=>setN(Number(e.target.value))} data-testid="rd-tenure"/></div>
      </div>
      <div className="border border-slate-200 rounded p-6 bg-slate-50">
        <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">Maturity Value</div>
        <div className="font-display text-5xl font-black text-slate-900 mt-1" data-testid="rd-result">{INR(A)}</div>
        <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
          <div><div className="text-slate-500">Total Invested</div><div className="font-bold">{INR(invested)}</div></div>
          <div><div className="text-slate-500">Interest Earned</div><div className="font-bold text-blue-700">{INR(interest)}</div></div>
        </div>
      </div>
    </div>
  );
}

/* ---------- SIP ---------- */
function SIPCalc() {
  const [M, setM] = useState(10000);
  const [R, setR] = useState(12);
  const [N, setN] = useState(10);
  const i = R / 12 / 100;
  const months = N * 12;
  const A = useMemo(() => M * ((Math.pow(1 + i, months) - 1) / i) * (1 + i), [M, i, months]);
  const invested = M * months;
  const gain = A - invested;
  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="sip-calc">
      <div className="space-y-4">
        <div><Label>Monthly SIP (₹)</Label><Input type="number" value={M} onChange={e=>setM(Number(e.target.value))} data-testid="sip-amount"/></div>
        <div><Label>Expected Return (% p.a.)</Label><Input type="number" step="0.5" value={R} onChange={e=>setR(Number(e.target.value))} data-testid="sip-rate"/></div>
        <div><Label>Investment Period (years)</Label><Input type="number" step="0.5" value={N} onChange={e=>setN(Number(e.target.value))} data-testid="sip-tenure"/></div>
      </div>
      <div className="border border-slate-200 rounded p-6 bg-slate-50">
        <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">Future Value</div>
        <div className="font-display text-5xl font-black text-slate-900 mt-1" data-testid="sip-result">{INR(A)}</div>
        <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
          <div><div className="text-slate-500">Total Invested</div><div className="font-bold">{INR(invested)}</div></div>
          <div><div className="text-slate-500">Wealth Gained</div><div className="font-bold text-blue-700">{INR(gain)}</div></div>
        </div>
        <p className="text-[11px] text-slate-500 mt-4">Assumes monthly compounding. Equity returns are market-linked; figures are illustrative.</p>
      </div>
    </div>
  );
}

/* ---------- Lumpsum ---------- */
function LumpsumCalc() {
  const [P, setP] = useState(500000);
  const [R, setR] = useState(12);
  const [N, setN] = useState(10);
  const A = useMemo(() => P * Math.pow(1 + R / 100, N), [P, R, N]);
  const gain = A - P;
  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="lumpsum-calc">
      <div className="space-y-4">
        <div><Label>Lumpsum Amount (₹)</Label><Input type="number" value={P} onChange={e=>setP(Number(e.target.value))} data-testid="ls-amount"/></div>
        <div><Label>Expected Return (% p.a.)</Label><Input type="number" step="0.5" value={R} onChange={e=>setR(Number(e.target.value))} data-testid="ls-rate"/></div>
        <div><Label>Period (years)</Label><Input type="number" step="0.5" value={N} onChange={e=>setN(Number(e.target.value))} data-testid="ls-tenure"/></div>
      </div>
      <div className="border border-slate-200 rounded p-6 bg-slate-50">
        <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">Future Value</div>
        <div className="font-display text-5xl font-black text-slate-900 mt-1" data-testid="ls-result">{INR(A)}</div>
        <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
          <div><div className="text-slate-500">Invested</div><div className="font-bold">{INR(P)}</div></div>
          <div><div className="text-slate-500">Wealth Gained</div><div className="font-bold text-blue-700">{INR(gain)}</div></div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Income Tax (FY 2024-25 / AY 2025-26) ---------- */
function calcNewRegime(taxable) {
  // Slabs FY 24-25 new regime (default)
  const slabs = [
    [300000, 0], [400000, 0.05], [300000, 0.10],
    [200000, 0.15], [300000, 0.20], [Infinity, 0.30]
  ];
  let rem = taxable, tax = 0;
  for (const [size, rate] of slabs) {
    if (rem <= 0) break;
    const x = Math.min(rem, size);
    tax += x * rate; rem -= x;
  }
  // 87A rebate up to ₹25,000 if taxable ≤ ₹7L
  if (taxable <= 700000) tax = Math.max(0, tax - 25000);
  return tax;
}
function calcOldRegime(taxable) {
  const slabs = [
    [250000, 0], [250000, 0.05], [500000, 0.20], [Infinity, 0.30]
  ];
  let rem = taxable, tax = 0;
  for (const [size, rate] of slabs) {
    if (rem <= 0) break;
    const x = Math.min(rem, size);
    tax += x * rate; rem -= x;
  }
  // 87A rebate up to ₹12,500 if taxable ≤ ₹5L
  if (taxable <= 500000) tax = Math.max(0, tax - 12500);
  return tax;
}
function TaxCalc() {
  const [income, setIncome] = useState(1500000);
  const [deductions80c, setD80c] = useState(150000);
  const [hra, setHra] = useState(0);
  const [otherDed, setOther] = useState(0);

  // New regime: only standard deduction ₹75,000 applies on salary
  const newStdDed = 75000;
  const newTaxable = Math.max(0, income - newStdDed);
  const newTax = calcNewRegime(newTaxable);
  const newCess = newTax * 0.04;
  const newTotal = newTax + newCess;

  // Old regime: std ded ₹50k + 80C + HRA + other
  const oldStdDed = 50000;
  const oldTaxable = Math.max(0, income - oldStdDed - deductions80c - hra - otherDed);
  const oldTax = calcOldRegime(oldTaxable);
  const oldCess = oldTax * 0.04;
  const oldTotal = oldTax + oldCess;

  const better = newTotal <= oldTotal ? "New" : "Old";
  const savings = Math.abs(newTotal - oldTotal);

  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="tax-calc">
      <div className="space-y-4">
        <div><Label>Annual Gross Income (₹) — salary</Label><Input type="number" value={income} onChange={e=>setIncome(Number(e.target.value))} data-testid="tax-income"/></div>
        <div className="text-xs uppercase tracking-widest text-slate-500 pt-2">Old regime deductions</div>
        <div><Label>80C / 80D / NPS etc. (₹)</Label><Input type="number" value={deductions80c} onChange={e=>setD80c(Number(e.target.value))} data-testid="tax-80c"/></div>
        <div><Label>HRA exemption (₹)</Label><Input type="number" value={hra} onChange={e=>setHra(Number(e.target.value))} data-testid="tax-hra-ex"/></div>
        <div><Label>Other deductions (₹)</Label><Input type="number" value={otherDed} onChange={e=>setOther(Number(e.target.value))} data-testid="tax-other"/></div>
        <p className="text-[11px] text-slate-500 pt-2">Slabs: FY 2024-25 (AY 2025-26). Cess 4%. Standard deduction ₹75,000 (new) / ₹50,000 (old). 87A rebate auto-applied.</p>
      </div>
      <div className="space-y-3">
        <div className="border border-slate-200 rounded p-5 bg-slate-50">
          <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">New Regime (default)</div>
          <div className="font-display text-3xl font-black text-slate-900 mt-1" data-testid="tax-new-total">{INR(newTotal)}</div>
          <div className="text-xs text-slate-500 mt-1">Taxable {INR(newTaxable)} · Tax {INR(newTax)} · Cess {INR(newCess)}</div>
        </div>
        <div className="border border-slate-200 rounded p-5 bg-slate-50">
          <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">Old Regime</div>
          <div className="font-display text-3xl font-black text-slate-900 mt-1" data-testid="tax-old-total">{INR(oldTotal)}</div>
          <div className="text-xs text-slate-500 mt-1">Taxable {INR(oldTaxable)} · Tax {INR(oldTax)} · Cess {INR(oldCess)}</div>
        </div>
        <div className="border border-blue-200 rounded p-5 bg-blue-50">
          <div className="text-xs uppercase tracking-widest text-blue-700 font-bold">Recommended</div>
          <div className="font-display text-2xl font-black text-blue-900 mt-1" data-testid="tax-better">{better} regime saves you {INR(savings)}</div>
        </div>
      </div>
    </div>
  );
}

/* ---------- HRA ---------- */
function HRACalc() {
  const [basic, setBasic] = useState(600000);   // annual basic+DA
  const [hra, setHra] = useState(300000);       // annual HRA received
  const [rent, setRent] = useState(360000);     // annual rent paid
  const [metro, setMetro] = useState(true);

  const cond1 = hra;
  const cond2 = basic * (metro ? 0.5 : 0.4);
  const cond3 = Math.max(0, rent - 0.1 * basic);
  const exempt = Math.min(cond1, cond2, cond3);
  const taxable = Math.max(0, hra - exempt);

  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="hra-calc">
      <div className="space-y-4">
        <div><Label>Basic + DA (annual ₹)</Label><Input type="number" value={basic} onChange={e=>setBasic(Number(e.target.value))} data-testid="hra-basic"/></div>
        <div><Label>HRA Received (annual ₹)</Label><Input type="number" value={hra} onChange={e=>setHra(Number(e.target.value))} data-testid="hra-received"/></div>
        <div><Label>Rent Paid (annual ₹)</Label><Input type="number" value={rent} onChange={e=>setRent(Number(e.target.value))} data-testid="hra-rent"/></div>
        <div>
          <Label>City type</Label>
          <div className="flex gap-2 mt-2">
            <Button type="button" variant={metro ? "default" : "outline"} size="sm" onClick={()=>setMetro(true)} data-testid="hra-metro">Metro (50%)</Button>
            <Button type="button" variant={!metro ? "default" : "outline"} size="sm" onClick={()=>setMetro(false)} data-testid="hra-nonmetro">Non-Metro (40%)</Button>
          </div>
        </div>
        <p className="text-[11px] text-slate-500">Section 10(13A). HRA exemption is the least of the three conditions; only available under the old regime.</p>
      </div>
      <div className="border border-slate-200 rounded p-6 bg-slate-50">
        <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">HRA Exempt</div>
        <div className="font-display text-5xl font-black text-slate-900 mt-1" data-testid="hra-result">{INR(exempt)}</div>
        <div className="grid grid-cols-1 gap-3 mt-6 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">1. Actual HRA</span><b>{INR(cond1)}</b></div>
          <div className="flex justify-between"><span className="text-slate-500">2. {metro ? "50%" : "40%"} of Basic+DA</span><b>{INR(cond2)}</b></div>
          <div className="flex justify-between"><span className="text-slate-500">3. Rent − 10% of Basic+DA</span><b>{INR(cond3)}</b></div>
          <div className="flex justify-between border-t border-slate-200 pt-2"><span className="text-slate-700 font-medium">Taxable HRA</span><b className="text-blue-700">{INR(taxable)}</b></div>
        </div>
      </div>
    </div>
  );
}


/* ---------- GST ---------- */
const GST_RATES = [0, 3, 5, 12, 18, 28];
function GSTCalc() {
  const [amount, setAmount] = useState(10000);
  const [rate, setRate] = useState(18);
  const [mode, setMode] = useState("exclusive"); // exclusive | inclusive
  const [interState, setInterState] = useState(false);

  const { net, gst, total } = useMemo(() => {
    if (mode === "inclusive") {
      const n = amount / (1 + rate / 100);
      return { net: n, gst: amount - n, total: amount };
    }
    const g = (amount * rate) / 100;
    return { net: amount, gst: g, total: amount + g };
  }, [amount, rate, mode]);

  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="gst-calc">
      <div className="space-y-4">
        <div><Label>Amount (₹)</Label><Input type="number" value={amount} onChange={e=>setAmount(Number(e.target.value))} data-testid="gst-amount"/></div>
        <div>
          <Label>GST Rate (%)</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {GST_RATES.map(r => (
              <Button key={r} type="button" size="sm" variant={rate===r?"default":"outline"} onClick={()=>setRate(r)} data-testid={`gst-rate-${r}`}>{r}%</Button>
            ))}
          </div>
        </div>
        <div>
          <Label>Mode</Label>
          <div className="flex gap-2 mt-2">
            <Button type="button" size="sm" variant={mode==="exclusive"?"default":"outline"} onClick={()=>setMode("exclusive")} data-testid="gst-exclusive">Add GST</Button>
            <Button type="button" size="sm" variant={mode==="inclusive"?"default":"outline"} onClick={()=>setMode("inclusive")} data-testid="gst-inclusive">Reverse (incl.)</Button>
          </div>
        </div>
        <div>
          <Label>Transaction</Label>
          <div className="flex gap-2 mt-2">
            <Button type="button" size="sm" variant={!interState?"default":"outline"} onClick={()=>setInterState(false)} data-testid="gst-intra">Intra-state (CGST + SGST)</Button>
            <Button type="button" size="sm" variant={interState?"default":"outline"} onClick={()=>setInterState(true)} data-testid="gst-inter">Inter-state (IGST)</Button>
          </div>
        </div>
      </div>
      <div className="border border-slate-200 rounded p-6 bg-slate-50">
        <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">Total (incl. GST)</div>
        <div className="font-display text-5xl font-black text-slate-900 mt-1" data-testid="gst-total">{INR(total)}</div>
        <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
          <div><div className="text-slate-500">Net (pre-GST)</div><div className="font-bold">{INR(net)}</div></div>
          <div><div className="text-slate-500">GST @ {rate}%</div><div className="font-bold text-blue-700">{INR(gst)}</div></div>
        </div>
        <div className="mt-5 pt-4 border-t border-slate-200 text-sm space-y-1">
          {interState ? (
            <div className="flex justify-between"><span className="text-slate-500">IGST @ {rate}%</span><b>{INR(gst)}</b></div>
          ) : (
            <>
              <div className="flex justify-between"><span className="text-slate-500">CGST @ {rate/2}%</span><b>{INR(gst/2)}</b></div>
              <div className="flex justify-between"><span className="text-slate-500">SGST @ {rate/2}%</span><b>{INR(gst/2)}</b></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- NPS ---------- */
function NPSCalc() {
  const [age, setAge] = useState(30);
  const [retire, setRetire] = useState(60);
  const [contrib, setContrib] = useState(10000);   // monthly
  const [ret, setRet] = useState(10);              // expected return %
  const [annuityPct, setAnnuityPct] = useState(40); // % of corpus into annuity (min 40)
  const [annuityRate, setAnnuityRate] = useState(6);

  const years = Math.max(0, retire - age);
  const months = years * 12;
  const i = ret / 12 / 100;
  const corpus = useMemo(() =>
    months > 0 ? contrib * ((Math.pow(1 + i, months) - 1) / i) * (1 + i) : 0,
    [contrib, i, months]);
  const invested = contrib * months;
  const wealth = corpus - invested;
  const annuityCorpus = corpus * (annuityPct / 100);
  const lumpsum = corpus - annuityCorpus;
  const monthlyPension = (annuityCorpus * (annuityRate / 100)) / 12;

  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="nps-calc">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Current Age</Label><Input type="number" value={age} onChange={e=>setAge(Number(e.target.value))} data-testid="nps-age"/></div>
          <div><Label>Retire At</Label><Input type="number" value={retire} onChange={e=>setRetire(Number(e.target.value))} data-testid="nps-retire"/></div>
        </div>
        <div><Label>Monthly Contribution (₹)</Label><Input type="number" value={contrib} onChange={e=>setContrib(Number(e.target.value))} data-testid="nps-contrib"/></div>
        <div><Label>Expected Return (% p.a.)</Label><Input type="number" step="0.5" value={ret} onChange={e=>setRet(Number(e.target.value))} data-testid="nps-return"/></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Annuity % at retirement</Label><Input type="number" min="40" max="100" value={annuityPct} onChange={e=>setAnnuityPct(Number(e.target.value))} data-testid="nps-annuity-pct"/></div>
          <div><Label>Annuity Rate (% p.a.)</Label><Input type="number" step="0.5" value={annuityRate} onChange={e=>setAnnuityRate(Number(e.target.value))} data-testid="nps-annuity-rate"/></div>
        </div>
        <p className="text-[11px] text-slate-500">Min 40% of NPS corpus must be used to buy annuity at 60. 60% lumpsum is tax-free.</p>
      </div>
      <div className="space-y-3">
        <div className="border border-slate-200 rounded p-5 bg-slate-50">
          <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">Corpus at Retirement</div>
          <div className="font-display text-4xl font-black text-slate-900 mt-1" data-testid="nps-corpus">{INR(corpus)}</div>
          <div className="text-xs text-slate-500 mt-2">Invested {INR(invested)} · Gain {INR(wealth)}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-slate-200 rounded p-4 bg-slate-50">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Lumpsum (60% def.)</div>
            <div className="font-display text-2xl font-black text-slate-900 mt-1" data-testid="nps-lumpsum">{INR(lumpsum)}</div>
          </div>
          <div className="border border-slate-200 rounded p-4 bg-slate-50">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Annuity Corpus</div>
            <div className="font-display text-2xl font-black text-slate-900 mt-1">{INR(annuityCorpus)}</div>
          </div>
        </div>
        <div className="border border-blue-200 rounded p-5 bg-blue-50">
          <div className="text-xs uppercase tracking-widest text-blue-700 font-bold">Estimated Monthly Pension</div>
          <div className="font-display text-3xl font-black text-blue-900 mt-1" data-testid="nps-pension">{INR(monthlyPension)}</div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Capital Gains (Equity, Debt, Property) ---------- */
function CapitalGainsCalc() {
  const [asset, setAsset] = useState("equity"); // equity | debt | property
  const [buy, setBuy] = useState(500000);
  const [sell, setSell] = useState(900000);
  const [holdMonths, setHoldMonths] = useState(18);

  const gain = sell - buy;
  let term = "STCG", rate = 0, exempt = 0, tax = 0, note = "";

  if (asset === "equity") {
    if (holdMonths >= 12) {
      term = "LTCG"; rate = 12.5; exempt = 125000;
      const taxable = Math.max(0, gain - exempt);
      tax = taxable * 0.125;
      note = "Listed equity / equity MF · LTCG @ 12.5% above ₹1.25L exemption (post Budget 2024).";
    } else {
      term = "STCG"; rate = 20;
      tax = Math.max(0, gain) * 0.20;
      note = "Listed equity STCG @ 20% (post Budget 2024).";
    }
  } else if (asset === "debt") {
    // Post 23-Jul-2024: all debt MF / unlisted bonds taxed at slab; for simplicity use 30% top slab here
    term = holdMonths >= 24 ? "LTCG" : "STCG";
    rate = 30;
    tax = Math.max(0, gain) * 0.30;
    note = "Debt MFs purchased after 1-Apr-2023 are taxed at slab rate; this assumes 30% top slab (configure as per your slab).";
  } else {
    // Property
    if (holdMonths >= 24) {
      term = "LTCG"; rate = 12.5;
      tax = Math.max(0, gain) * 0.125;
      note = "Immovable property LTCG @ 12.5% without indexation (post 23-Jul-2024). Properties bought before may opt for 20% with indexation.";
    } else {
      term = "STCG"; rate = 30;
      tax = Math.max(0, gain) * 0.30;
      note = "STCG on property is taxed at slab rate; assumed 30% here.";
    }
  }
  const cess = tax * 0.04;
  const total = tax + cess;

  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="cg-calc">
      <div className="space-y-4">
        <div>
          <Label>Asset Class</Label>
          <div className="flex gap-2 mt-2">
            {[["equity","Equity / Equity MF"],["debt","Debt MF / Bonds"],["property","Property"]].map(([k,l])=>(
              <Button key={k} type="button" size="sm" variant={asset===k?"default":"outline"} onClick={()=>setAsset(k)} data-testid={`cg-${k}`}>{l}</Button>
            ))}
          </div>
        </div>
        <div><Label>Buy / Cost (₹)</Label><Input type="number" value={buy} onChange={e=>setBuy(Number(e.target.value))} data-testid="cg-buy"/></div>
        <div><Label>Sell Value (₹)</Label><Input type="number" value={sell} onChange={e=>setSell(Number(e.target.value))} data-testid="cg-sell"/></div>
        <div><Label>Holding Period (months)</Label><Input type="number" value={holdMonths} onChange={e=>setHoldMonths(Number(e.target.value))} data-testid="cg-months"/></div>
        <p className="text-[11px] text-slate-500">{note}</p>
      </div>
      <div className="border border-slate-200 rounded p-6 bg-slate-50">
        <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">{term} Tax Liability</div>
        <div className="font-display text-5xl font-black text-slate-900 mt-1" data-testid="cg-total">{INR(total)}</div>
        <div className="grid grid-cols-1 gap-2 mt-6 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Gain</span><b>{INR(gain)}</b></div>
          {exempt > 0 && <div className="flex justify-between"><span className="text-slate-500">Exemption</span><b>{INR(exempt)}</b></div>}
          <div className="flex justify-between"><span className="text-slate-500">Rate</span><b>{rate}%</b></div>
          <div className="flex justify-between"><span className="text-slate-500">Tax</span><b>{INR(tax)}</b></div>
          <div className="flex justify-between border-t border-slate-200 pt-2"><span className="text-slate-500">Cess (4%)</span><b className="text-blue-700">{INR(cess)}</b></div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Loan Eligibility ---------- */
function LoanEligibilityCalc() {
  const [income, setIncome] = useState(80000);     // monthly
  const [existing, setExisting] = useState(5000);  // existing EMI obligations
  const [foir, setFoir] = useState(50);            // % of income to EMI
  const [rate, setRate] = useState(9.5);
  const [years, setYears] = useState(20);

  const eligibleEMI = Math.max(0, income * (foir / 100) - existing);
  const r = rate / 12 / 100;
  const n = years * 12;
  const eligibleLoan = useMemo(() =>
    r > 0 ? (eligibleEMI * (1 - Math.pow(1 + r, -n))) / r : eligibleEMI * n,
    [eligibleEMI, r, n]);

  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="loan-eligibility-calc">
      <div className="space-y-4">
        <div><Label>Monthly Net Income (₹)</Label><Input type="number" value={income} onChange={e=>setIncome(Number(e.target.value))} data-testid="le-income"/></div>
        <div><Label>Existing Monthly EMI (₹)</Label><Input type="number" value={existing} onChange={e=>setExisting(Number(e.target.value))} data-testid="le-existing"/></div>
        <div><Label>FOIR (% of income to EMI): {foir}%</Label>
          <input type="range" min="30" max="65" value={foir} onChange={e=>setFoir(Number(e.target.value))} className="w-full mt-2" data-testid="le-foir"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Interest Rate (%)</Label><Input type="number" step="0.1" value={rate} onChange={e=>setRate(Number(e.target.value))} data-testid="le-rate"/></div>
          <div><Label>Tenure (years)</Label><Input type="number" value={years} onChange={e=>setYears(Number(e.target.value))} data-testid="le-tenure"/></div>
        </div>
        <p className="text-[11px] text-slate-500">FOIR (Fixed Obligations to Income Ratio) banks usually allow 50-55% for salaried, up to 65% for high-income borrowers.</p>
      </div>
      <div className="space-y-3">
        <div className="border border-slate-200 rounded p-5 bg-slate-50">
          <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">Eligible Loan Amount</div>
          <div className="font-display text-5xl font-black text-slate-900 mt-1" data-testid="le-loan">{INR(eligibleLoan)}</div>
        </div>
        <div className="border border-blue-200 rounded p-5 bg-blue-50">
          <div className="text-xs uppercase tracking-widest text-blue-700 font-bold">Affordable Monthly EMI</div>
          <div className="font-display text-3xl font-black text-blue-900 mt-1" data-testid="le-emi">{INR(eligibleEMI)}</div>
          <div className="text-xs text-slate-600 mt-2">{foir}% of {INR(income)} − existing {INR(existing)}</div>
        </div>
      </div>
    </div>
  );
}


/* ---------- Quick Links ---------- */
const LINKS = [
  { group: "Income Tax", icon: Receipt, color: "text-blue-600", items: [
    ["Income Tax e-Filing Portal", "https://www.incometax.gov.in/iec/foportal/"],
    ["TRACES (Form 26AS, TDS)", "https://www.tdscpc.gov.in/"],
    ["TIN-NSDL e-Pay Tax", "https://onlineservices.tin.egov-nsdl.com/etaxnew/tdsnontds.jsp"],
    ["Refund Status", "https://tin.tin.nsdl.com/oltas/refundstatuslogin.html"],
    ["AIS Compliance Portal", "https://compliance.insight.gov.in/"],
  ]},
  { group: "GST", icon: ScrollText, color: "text-emerald-600", items: [
    ["GST Portal (gst.gov.in)", "https://www.gst.gov.in/"],
    ["GST e-Invoice Portal", "https://einvoice.gst.gov.in/"],
    ["E-Way Bill Portal", "https://ewaybillgst.gov.in/"],
    ["GST Search Taxpayer", "https://services.gst.gov.in/services/searchtp"],
    ["GSTN Help & FAQ", "https://tutorial.gst.gov.in/"],
  ]},
  { group: "PAN / TAN / NSDL", icon: FileText, color: "text-purple-600", items: [
    ["PAN Apply (Protean / NSDL)", "https://www.onlineservices.nsdl.com/paam/endUserRegisterContact.html"],
    ["PAN Apply (UTIITSL)", "https://www.pan.utiitsl.com/PAN/"],
    ["TAN Apply / Track", "https://tin.tin.nsdl.com/tan/"],
    ["NSDL e-Gov (Protean)", "https://www.protean-tinpan.com/"],
    ["Aadhaar–PAN Link Status", "https://eportal.incometax.gov.in/iec/foservices/#/pre-login/link-aadhaar-status"],
  ]},
  { group: "EPFO (PF) & ESIC", icon: ShieldCheck, color: "text-orange-600", items: [
    ["EPFO Member Portal", "https://unifiedportal-mem.epfindia.gov.in/memberinterface/"],
    ["EPFO Employer Portal", "https://unifiedportal-emp.epfindia.gov.in/epfo/"],
    ["UAN Member Passbook", "https://passbook.epfindia.gov.in/"],
    ["ESIC Portal", "https://www.esic.gov.in/"],
    ["ESIC Employer Login", "https://www.esic.in/EmployerPortal/ESICInsurance1Portal/"],
  ]},
  { group: "MCA / ROC / DGFT", icon: Building2, color: "text-rose-600", items: [
    ["MCA Portal (V3)", "https://www.mca.gov.in/"],
    ["MCA Master Data", "https://www.mca.gov.in/mcafoportal/viewCompanyMasterData.do"],
    ["DSC – eMudhra", "https://www.e-mudhra.com/"],
    ["DGFT (IEC)", "https://www.dgft.gov.in/CP/"],
    ["MSME / Udyam Registration", "https://udyamregistration.gov.in/"],
  ]},
  { group: "Maharashtra Labour", icon: Briefcase, color: "text-cyan-700", items: [
    ["MLWF (mlwb.in)", "https://mlwb.maharashtra.gov.in/"],
    ["Maharashtra PT (mahagst)", "https://mahagst.gov.in/en"],
    ["Shop & Establishment (LMS)", "https://lms.mahaonline.gov.in/"],
    ["Maharashtra Labour Dept.", "https://mahakamgar.maharashtra.gov.in/"],
  ]},
  { group: "USA Filings", icon: Globe2, color: "text-blue-700", items: [
    ["IRS (irs.gov)", "https://www.irs.gov/"],
    ["EIN Online", "https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online"],
    ["Delaware Division of Corporations", "https://corp.delaware.gov/"],
    ["Wyoming Secretary of State", "https://sos.wyo.gov/Business/"],
    ["California SOS", "https://bizfileonline.sos.ca.gov/"],
  ]},
  { group: "Banking & Loans", icon: Landmark, color: "text-emerald-700", items: [
    ["RBI", "https://www.rbi.org.in/"],
    ["CIBIL Score", "https://www.cibil.com/"],
    ["SIDBI (MSME loans)", "https://www.sidbi.in/"],
    ["PM Mudra Yojana", "https://www.mudra.org.in/"],
    ["CGTMSE", "https://www.cgtmse.in/"],
  ]},
  { group: "Other Useful", icon: GraduationCap, color: "text-slate-700", items: [
    ["DigiLocker", "https://www.digilocker.gov.in/"],
    ["FSSAI Registration", "https://www.fssai.gov.in/"],
    ["Trademark Search (IPIndia)", "https://ipindiaonline.gov.in/tmrpublicsearch/frmmain.aspx"],
    ["RERA (MahaRERA)", "https://maharera.maharashtra.gov.in/"],
    ["NITI Aayog NGO Darpan", "https://ngodarpan.gov.in/"],
  ]},
];

export default function Tools() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="tools-page">
      <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-bold">Tools & Resources</div>
      <h1 className="font-display text-5xl font-black tracking-tight text-slate-900 mb-2">Calculators & quick links.</h1>
      <p className="text-slate-600 max-w-2xl">EMI, FD and RD calculators, plus a curated directory of every government portal you'll ever need — Income Tax, GST, NSDL, TRACES, EPFO, ESIC, MCA, MLWF and more.</p>

      {/* Calculators */}
      <div className="border border-slate-200 rounded bg-white mt-10" data-testid="calculators">
        <div className="p-6 border-b border-slate-200 flex items-center gap-2">
          <Calculator className="w-5 h-5 text-blue-600"/>
          <h2 className="font-display text-2xl font-bold text-slate-900">Financial Calculators</h2>
        </div>
        <div className="p-6">
          <Tabs defaultValue="emi">
            <TabsList className="flex flex-wrap h-auto justify-start gap-1 mb-6 bg-slate-100 p-1">
              <TabsTrigger value="emi" data-testid="tab-emi"><Banknote className="w-4 h-4 mr-1"/>EMI</TabsTrigger>
              <TabsTrigger value="fd" data-testid="tab-fd"><PiggyBank className="w-4 h-4 mr-1"/>FD</TabsTrigger>
              <TabsTrigger value="rd" data-testid="tab-rd"><Scale className="w-4 h-4 mr-1"/>RD</TabsTrigger>
              <TabsTrigger value="sip" data-testid="tab-sip"><TrendingUp className="w-4 h-4 mr-1"/>SIP</TabsTrigger>
              <TabsTrigger value="lumpsum" data-testid="tab-lumpsum"><Coins className="w-4 h-4 mr-1"/>Lumpsum</TabsTrigger>
              <TabsTrigger value="tax" data-testid="tab-tax"><Receipt className="w-4 h-4 mr-1"/>Income Tax</TabsTrigger>
              <TabsTrigger value="hra" data-testid="tab-hra"><Home className="w-4 h-4 mr-1"/>HRA</TabsTrigger>
              <TabsTrigger value="gst" data-testid="tab-gst"><Percent className="w-4 h-4 mr-1"/>GST</TabsTrigger>
              <TabsTrigger value="nps" data-testid="tab-nps"><Wallet className="w-4 h-4 mr-1"/>NPS</TabsTrigger>
              <TabsTrigger value="cg" data-testid="tab-cg"><LineChart className="w-4 h-4 mr-1"/>Capital Gains</TabsTrigger>
              <TabsTrigger value="le" data-testid="tab-le"><BadgeIndianRupee className="w-4 h-4 mr-1"/>Loan Eligibility</TabsTrigger>
            </TabsList>
            <TabsContent value="emi"><EMICalc/></TabsContent>
            <TabsContent value="fd"><FDCalc/></TabsContent>
            <TabsContent value="rd"><RDCalc/></TabsContent>
            <TabsContent value="sip"><SIPCalc/></TabsContent>
            <TabsContent value="lumpsum"><LumpsumCalc/></TabsContent>
            <TabsContent value="tax"><TaxCalc/></TabsContent>
            <TabsContent value="hra"><HRACalc/></TabsContent>
            <TabsContent value="gst"><GSTCalc/></TabsContent>
            <TabsContent value="nps"><NPSCalc/></TabsContent>
            <TabsContent value="cg"><CapitalGainsCalc/></TabsContent>
            <TabsContent value="le"><LoanEligibilityCalc/></TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Quick links */}
      <div className="mt-12">
        <h2 className="font-display text-3xl font-black text-slate-900 mb-1">Quick links</h2>
        <p className="text-slate-600 mb-6 text-sm">All bookmarks, one shelf. Opens in a new tab.</p>
        <div className="grid md:grid-cols-3 gap-5" data-testid="quick-links-grid">
          {LINKS.map((g) => (
            <div key={g.group} className="tracing-card border border-slate-200 rounded p-5 bg-white" data-testid={`link-group-${g.group.replace(/\s/g,'-').toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-3">
                <g.icon className={`w-5 h-5 ${g.color}`}/>
                <h3 className="font-bold text-slate-900">{g.group}</h3>
              </div>
              <ul className="space-y-2 text-sm">
                {g.items.map(([label, href]) => (
                  <li key={href}>
                    <a href={href} target="_blank" rel="noreferrer"
                       className="flex items-center justify-between gap-2 text-slate-700 hover:text-blue-700 hover:underline">
                      <span>{label}</span>
                      <ExternalLink className="w-3.5 h-3.5 shrink-0 text-slate-400"/>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
